/**
 * Where to go after sign-in (`?next=`). Allowed: a same-origin path, or an absolute URL on the
 * app origin or on a trusted backend origin (the OAuth issuer / API host, where the MCP consent
 * flow's /oauth/authorize lives). Anything else falls back to "/" (open-redirect protection).
 */
export type NextTarget = { kind: 'app'; path: string } | { kind: 'external'; url: string };

const HOME: NextTarget = { kind: 'app', path: '/' };

/** Origins configured at build time: VITE_OAUTH_ISSUER_URL and VITE_API_BASE_URL. */
export function trustedOrigins(
  env: Record<string, string | undefined> = import.meta.env,
): string[] {
  const out: string[] = [];
  for (const v of [env.VITE_OAUTH_ISSUER_URL, env.VITE_API_BASE_URL]) {
    if (!v) continue;
    try {
      const u = new URL(v);
      if (u.protocol === 'https:' || u.protocol === 'http:') out.push(u.origin);
    } catch {
      /* ignore invalid configuration */
    }
  }
  return out;
}

export function resolveNext(
  next: string | null | undefined,
  appOrigin: string = window.location.origin,
  trusted: string[] = trustedOrigins(),
): NextTarget {
  if (!next) return HOME;
  // Control characters and backslashes are how browsers get tricked into other hosts.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(next)) return HOME;
  if (next.startsWith('/')) {
    if (next.startsWith('//')) return HOME;
    let url: URL;
    try {
      url = new URL(next, appOrigin);
    } catch {
      return HOME;
    }
    if (url.origin !== appOrigin) return HOME;
    return toTarget(url, appOrigin);
  }
  let url: URL;
  try {
    url = new URL(next);
  } catch {
    return HOME;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return HOME;
  if (url.username || url.password) return HOME;
  if (url.origin !== appOrigin && !trusted.includes(url.origin)) return HOME;
  return toTarget(url, appOrigin);
}

/** Backend routes on the app origin (proxied) need a full page load, not a client navigation. */
const SERVER_PATHS = /^\/(?:oauth|api|mcp|\.well-known)(?:\/|$)/;

function toTarget(url: URL, appOrigin: string): NextTarget {
  const path = `${url.pathname}${url.search}${url.hash}`;
  if (url.origin === appOrigin && !SERVER_PATHS.test(url.pathname)) return { kind: 'app', path };
  return { kind: 'external', url: url.toString() };
}

/** Follows a resolved target: client-side for app routes, a full load otherwise. */
export function goNext(target: NextTarget, navigate: (path: string) => void): void {
  if (target.kind === 'app') navigate(target.path);
  else window.location.assign(target.url);
}
